import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import {
  ICreateZoneRequest,
  IUpdateZoneRequest,
  IDeleteZoneRequest,
  ICreateSafeLandZoneRequest,
  IUpdateSafeLandZoneRequest,
  IDeleteSafeLandZoneRequest,
} from "./types";
import { baseZonesApi, ZoneEntity, SafeLandZoneEntity } from '@src/core/features';
export { useGetSafeLandZonesQuery } from '@src/core/features/zones/redux/api';

const updateSafeLandCache = baseZonesApi.util.updateQueryData as unknown as (
  endpointName: 'getSafeLandZones' | 'getSafeLandZone',
  arg: undefined | number,
  updateRecipe: (draft: SafeLandZoneEntity[] | SafeLandZoneEntity) => void,
) => ThunkAction<{ undo: () => void }, unknown, unknown, UnknownAction>;

export const zonesFeatureApi = baseZonesApi.injectEndpoints({
  endpoints: (builder) => ({
    getZone: builder.query<ZoneEntity, number>({
      query: (zoneId) => ({
        url: `/api/no-fly-zones/${zoneId}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),

    createZone: builder.mutation<ZoneEntity, ICreateZoneRequest>({
      invalidatesTags: ['GetZones'],
      query: (data) => ({
        url: '/api/no-fly-zones',
        method: 'POST',
        data,
      }),
    }),

    updateZone: builder.mutation<ZoneEntity, IUpdateZoneRequest>({
      invalidatesTags: ['GetZones'],
      query: (data) => ({
        url: `/api/no-fly-zones/${data.id}`,
        method: 'PUT',
        data,
      }),
    }),

    deleteZone: builder.mutation<void, IDeleteZoneRequest>({
      invalidatesTags: ['GetZones'],
      query: (data) => ({
        url: `/api/no-fly-zones/${data.id}`,
        method: 'DELETE',
      }),
    }),

    getSafeLandZone: builder.query<SafeLandZoneEntity, number>({
      providesTags: (_r, _e, id) => [{ type: 'SafeLandZones' as const, id }],
      query: (zoneId) => ({
        url: `/api/safe-land-zones/${zoneId}`,
        method: 'GET',
      }),
    }),
    createSafeLandZone: builder.mutation<SafeLandZoneEntity, ICreateSafeLandZoneRequest>({
      invalidatesTags: ['SafeLandZones'],
      query: (data) => ({
        url: '/api/safe-land-zones',
        method: 'POST',
        data,
      }),
    }),
    updateSafeLandZone: builder.mutation<SafeLandZoneEntity, IUpdateSafeLandZoneRequest>({
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const optimisticFields = {
          name: arg.name,
          description: arg.description,
          centerLatitude: arg.centerLatitude,
          centerLongitude: arg.centerLongitude,
          locationName: arg.locationName,
          altitudeMeters: arg.altitudeMeters,
          isActive: arg.isActive,
        };

        const patchList = dispatch(
          updateSafeLandCache('getSafeLandZones', undefined, (draft) => {
            const list = draft as SafeLandZoneEntity[];
            const i = list.findIndex((z) => z.id === arg.id);
            if (i !== -1) Object.assign(list[i], optimisticFields);
          }),
        );

        const patchDetail = dispatch(
          updateSafeLandCache('getSafeLandZone', arg.id, (draft) => {
            Object.assign(draft as SafeLandZoneEntity, optimisticFields);
          }),
        );

        try {
          const { data } = await queryFulfilled;
          dispatch(
            updateSafeLandCache('getSafeLandZones', undefined, (draft) => {
              const list = draft as SafeLandZoneEntity[];
              const i = list.findIndex((z) => z.id === data.id);
              if (i !== -1) list[i] = data;
            }),
          );
          dispatch(
            updateSafeLandCache('getSafeLandZone', data.id, () => data),
          );
        } catch {
          patchList.undo();
          patchDetail.undo();
        }
      },
      query: ({ id, ...body }) => ({
        url: `/api/safe-land-zones/${id}`,
        method: 'PUT',
        data: body,
      }),
    }),
    deleteSafeLandZone: builder.mutation<void, IDeleteSafeLandZoneRequest>({
      invalidatesTags: ['SafeLandZones'],
      query: ({ id }) => ({
        url: `/api/safe-land-zones/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetZoneQuery,
  useCreateZoneMutation,
  useUpdateZoneMutation,
  useDeleteZoneMutation,
  useGetSafeLandZoneQuery,
  useCreateSafeLandZoneMutation,
  useUpdateSafeLandZoneMutation,
  useDeleteSafeLandZoneMutation,
} = zonesFeatureApi;
